import { PaymentDetailResponse } from './payment.types';

export interface PaymentFailure {
    title: string;
    message: string;
}

/**
 * Ameria vPOS response codes -> what to tell the donor.
 *
 * Gateway declines arrive as "0" + ActionCode (e.g. ActionCode 116 -> "0116"),
 * 3-D Secure / session problems as "0-NNNN". Reference:
 * https://gist.github.com/hos/4ed551472aeb6be0ab4b29170d3fd726
 * Only codes a donor can act on are mapped; everything else falls back to a
 * generic decline.
 */
const FAILURES: Record<string, PaymentFailure> = {};

function register(codes: string[], failure: PaymentFailure) {
    codes.forEach((code) => { FAILURES[code] = failure; });
}

register(['0116'], {
    title: 'Insufficient funds',
    message: 'Your card does not have enough funds for this payment. Please top up or use another card.',
});

register(['0101'], {
    title: 'Card expired',
    message: 'The card you used has expired. Please try again with a valid card.',
});

register(['0111', '0125', '071015', '02', '0913'], {
    title: 'Card details not accepted',
    message: 'The card details could not be verified. Please check the card number, expiry date and CVC and try again.',
});

register(['0121', '0123', '0209', '0903', '0-2002', '0-20010'], {
    title: 'Card limit exceeded',
    message: 'This payment exceeds a limit set on your card. Please try a smaller amount, another card, or contact your bank.',
});

register(['0100', '0104', '0120', '0902', '02003', '0107', '0208'], {
    title: 'Declined by your bank',
    message: 'Your bank did not allow this online payment. Please contact your bank or try another card.',
});

register(['0-2005', '0-2006', '0-2009', '0-2011', '0-2015', '0-2016', '0-2019', '02005', '0999'], {
    title: 'Verification not completed',
    message: 'The security check with your bank was not completed. Please try again and confirm the payment when your bank asks.',
});

register(['0-100', '0-2007', '0-2013', '01001'], {
    title: 'Payment session expired',
    message: 'The payment page timed out before the payment was completed. Nothing has been charged. Please start again.',
});

register(['0-1', '0103', '0907', '0909', '0910', '0151017', '0151018', '0151019', '07', '500', '550', '560'], {
    title: 'Bank temporarily unavailable',
    message: 'Your bank could not process the payment right now. Nothing has been charged. Please try again in a few minutes.',
});

register(['0-2000', '0-2001', '02001', '0341014'], {
    title: 'Payment declined',
    message: 'This payment was declined for security reasons. Please contact your bank or try another card.',
});

const GENERIC_DECLINE: PaymentFailure = {
    title: 'Payment not completed',
    message: 'Your payment was not completed and nothing has been charged. Please try again or use another card.',
};

function candidates(details: Partial<PaymentDetailResponse>): string[] {
    const out: string[] = [];
    const push = (v: unknown) => {
        if (v === undefined || v === null || v === '') return;
        const s = String(v).trim();
        out.push(s);
        // "116" -> "0116", "-2009" -> "0-2009"
        if (!s.startsWith('0')) out.push(`0${s}`);
    };
    push(details.ResponseCode);
    push(details.ActionCode);
    return out;
}

/** Maps a failed GetPaymentDetails response to a donor-facing title and message. */
export function describePaymentFailure(details?: Partial<PaymentDetailResponse> | null): PaymentFailure {
    if (!details) return GENERIC_DECLINE;

    for (const code of candidates(details)) {
        const match = FAILURES[code];
        if (match) return match;
    }

    return GENERIC_DECLINE;
}
